/**
 * SharedRecordReviewPage - /shared-records (US-061-08)
 *
 * One list of shared-layer records waiting for review: Proposed (first batch per
 * business type) and Flagged (fresh value contradicts the stored one).
 */
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import {
  approveSharedRecord,
  listSharedRecords,
  rejectSharedRecord,
  SharedRecordReviewError,
  type SharedRecord,
  type SharedRecordStatus,
} from '../api/sharedRecordReviewClient';
import { SharedRecordReviewTable } from '../components/sharedRecords/SharedRecordReviewTable';
import { SharedRecordDetail } from '../components/sharedRecords/SharedRecordDetail';
import { useLatestRequest } from '../hooks/useLatestRequest';

const PAGE_SIZE = 25;
const FILTERS = ['All', 'Proposed', 'Flagged'] as const;
type Filter = (typeof FILTERS)[number];

function message(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

export function SharedRecordReviewPage() {
  const [records, setRecords] = useState<SharedRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const beginLoad = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginLoad();
    try {
      const status: SharedRecordStatus | undefined = filter === 'All' ? undefined : filter;
      const result = await listSharedRecords(status, page, PAGE_SIZE);
      if (!isCurrent()) return;
      setRecords(result.items);
      setTotal(result.total);
      setSelectedId((cur) => (result.items.some((r) => r.recordId === cur) ? cur : (result.items[0]?.recordId ?? null)));
      setLoadError(null);
    } catch (e) {
      if (!isCurrent()) return;
      setLoadError(message(e, 'Could not load the review queue.'));
    }
    setIsLoading(false);
  }, [beginLoad, filter, page]);

  useEffect(() => {
    // Fetch on mount / filter / page change; state is set only after the await.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const selected = records.find((r) => r.recordId === selectedId) ?? null;

  const act = async (record: SharedRecord, run: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setActionError(null);
    setConfirmation(null);
    try {
      await run();
      const remaining = records.filter((r) => r.recordId !== record.recordId);
      setRecords(remaining);
      setTotal((t) => Math.max(0, t - 1));
      setSelectedId(remaining[0]?.recordId ?? null);
      setConfirmation(`${done}: ${record.key}`);
      if (remaining.length === 0 && total > 1) {
        if (page > 1) setPage(page - 1);
        else await load();
      }
    } catch (e) {
      setActionError(message(e, 'Could not save the review.'));
      if (e instanceof SharedRecordReviewError && (e.status === 404 || e.status === 409)) {
        await load();
      }
    }
    setBusy(false);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isEmpty = !isLoading && !loadError && records.length === 0;

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Shared record review</h1>
          <p className="text-xs text-zinc-500">Proposed and flagged records waiting for an admin</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={isLoading}
          aria-label="Refresh shared records"
          className="inline-flex min-h-[48px] items-center gap-1.5 rounded-lg bg-zinc-800 px-3 text-xs text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          )}
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by status">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            onClick={() => {
              setFilter(f);
              setPage(1);
            }}
            className={`min-h-[48px] rounded-full border px-4 text-xs transition-colors ${
              filter === f
                ? 'border-violet-500 bg-violet-500/15 text-violet-200'
                : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {confirmation && (
        <p role="status" className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          {confirmation}
        </p>
      )}

      {loadError && (
        <p role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {loadError}
        </p>
      )}

      {isLoading && records.length === 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" aria-hidden="true" /> Loading records…
        </div>
      )}

      {isEmpty && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-400" aria-hidden="true" />
          <p className="text-sm text-zinc-300">Nothing is waiting for review.</p>
        </div>
      )}

      {records.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <SharedRecordReviewTable records={records} selectedId={selectedId} onSelect={setSelectedId} />
            {pageCount > 1 && (
              <nav aria-label="Pagination" className="mt-3 flex items-center justify-between text-xs text-zinc-400">
                <button
                  type="button"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((p) => p - 1)}
                  className="min-h-[48px] rounded-lg border border-zinc-700 px-4 disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {pageCount}
                </span>
                <button
                  type="button"
                  disabled={page >= pageCount || isLoading}
                  onClick={() => setPage((p) => p + 1)}
                  className="min-h-[48px] rounded-lg border border-zinc-700 px-4 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            )}
          </div>
          {selected && (
            <SharedRecordDetail
              key={selected.recordId}
              record={selected}
              busy={busy}
              error={actionError}
              onApprove={() => void act(selected, () => approveSharedRecord(selected.recordId), 'Approved')}
              onReject={(reason) => void act(selected, () => rejectSharedRecord(selected.recordId, reason), 'Rejected')}
            />
          )}
        </div>
      )}
    </main>
  );
}

export default SharedRecordReviewPage;
